/**
 * Login form component with validation
 */
import React, { useState } from 'react';
import { useAuth, LoginCredentials, AuthError } from '../../contexts/AuthContext';
import { orDefault } from './authCopy';
import { FmBox, FmButton, FmText, FmBanner, FmSeparator, FmStack, FmForm } from '@/components/ui';
import { useLanguage } from '@/i18n';
import type { FmFormSectionDescriptor } from '@/components/ui';

interface LoginFormProps {
  onSuccess?: () => void;
  onSwitchToRegister?: () => void;
}

type AuthCopy = ReturnType<typeof useLanguage>['t']['auth'] | undefined;

/** Resolve all localized login-form copy with English fallbacks (kept out of the
 * component body so its JSX stays free of `||`-fallback branching). */
function resolveLoginCopy(rawAuth: AuthCopy) {
  const auth: Partial<NonNullable<AuthCopy>> = rawAuth ?? {};
  const validation: Partial<NonNullable<NonNullable<AuthCopy>['validation']>> =
    auth.validation ?? {};
  return {
    title: orDefault(auth.loginTitle, 'Sign In'),
    subtitle: orDefault(auth.loginSubtitle, 'Access your forest properties and data'),
    email: orDefault(auth.email, 'Email'),
    emailPlaceholder: orDefault(auth.emailPlaceholder, 'Enter your email'),
    emailRequired: orDefault(validation.emailRequired, 'Please enter your email'),
    emailInvalid: orDefault(validation.emailInvalid, 'Please enter a valid email'),
    password: orDefault(auth.password, 'Password'),
    passwordPlaceholder: orDefault(auth.passwordPlaceholder, 'Enter your password'),
    passwordRequired: orDefault(validation.passwordRequired, 'Please enter your password'),
    passwordMin6: orDefault(validation.passwordMin6, 'Password must be at least 6 characters'),
    loginButton: orDefault(auth.loginButton, 'Sign In'),
    orDivider: orDefault(auth.orDivider, 'or'),
    noAccountPrompt: orDefault(auth.noAccountPrompt, "Don't have an account?"),
    switchToRegister: orDefault(auth.switchToRegister, 'Create Account'),
  };
}

export const LoginForm: React.FC<LoginFormProps> = ({ onSuccess, onSwitchToRegister }) => {
  const { login, isLoading } = useAuth();
  const { t } = useLanguage();
  const [error, setError] = useState<string | null>(null);

  const copy = resolveLoginCopy(t?.auth);

  const handleLogin = async (values: Record<string, unknown>) => {
    try {
      setError(null);
      await login(values as unknown as LoginCredentials);
      onSuccess?.();
    } catch (err) {
      if (err instanceof AuthError) {
        const code = err.code;
        const errors = t?.auth?.errors as Partial<Record<string, string>> | undefined;
        const localized = errors?.[code];
        setError(localized || t?.auth?.loginFailed || 'Login failed');
      } else {
        setError(t?.auth?.loginFailed || 'Login failed');
      }
    }
  };

  const formSections: FmFormSectionDescriptor[] = [
    {
      title: copy.title,
      description: copy.subtitle,
      fieldDescriptors: [
        {
          kind: 'text',
          name: 'email',
          label: copy.email,
          placeholder: copy.emailPlaceholder,
          type: 'email',
          required: true,
          validation: {
            rules: [
              { type: 'email', message: copy.emailInvalid },
            ],
            required: true,
            message: copy.emailRequired,
          },
          testId: 'login-email',
        },
        {
          kind: 'text',
          name: 'password',
          label: copy.password,
          placeholder: copy.passwordPlaceholder,
          type: 'password',
          required: true,
          validation: {
            rules: [
              { min: 6, message: copy.passwordMin6 },
            ],
            required: true,
            message: copy.passwordRequired,
          },
          testId: 'login-password',
        },
      ],
    },
  ];

  return (
    <FmStack gap="lg">
      {error && (
        <FmBox>
          <FmBanner tone="error" title={error} data-testid="login-error" />
        </FmBox>
      )}

      <FmForm
        sections={formSections}
        onSubmit={handleLogin}
        submit={{
          label: copy.loginButton,
          disabled: isLoading,
          testId: 'login-submit',
        }}
        disabled={isLoading}
        density="comfortable"
        testId="login-form"
      />

      {onSwitchToRegister && (
        <>
          <FmText size="caption" tone="muted">{copy.orDivider}</FmText>
          <FmSeparator />
          <FmStack align="center" gap="xs">
            <FmText as="span" tone="subtle">{copy.noAccountPrompt} </FmText>
            <FmButton variant="link" size="md" onClick={onSwitchToRegister}>
              {copy.switchToRegister}
            </FmButton>
          </FmStack>
        </>
      )}
    </FmStack>
  );
};
